// deps
import React, { ReactElement, memo, useContext, useCallback } from "react";

// internal deps
import { LoadingScreen, TransactionProgressLoader, VerifyCvv } from "@swiggy-private/payment-ui";
import { CardDetails, ProceedNewCardDetails, VALID_PAYMENT_METHODS } from "@swiggy-private/payment-types";
import {
    GlobalHeaderState,
    SwgyHeaderKeysList,
    CheckoutActionTypes,
    CheckoutContext,
    recordNREvent,
    NEWRELIC_TABLES,
} from "@swiggy-private/checkout-core";
import { useEffectOnce } from "@swiggy-private/core-ui";
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// components
import PaymentModesWrapper from "../payment-modes-wrapper";
import { GenericError, PAYMENT_INITIAL_STATE } from "@payment-client";

// contexts
import { PaymentModesContext } from "@payment-client/contexts/PaymentModesContext";
import { TransactionContext } from "@payment-client/contexts/TransactionContext";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";

// hooks
import { useOnProcess } from "@payment-client/hooks/UseOnProcess";

// helpers
import { getSelectedCardDetails } from "@payment-client/helpers/ManageCard";
import { getOnProcessParams } from "@payment-client/helpers/TransactionHelper";
import { getPaymentErrorScreenAction } from "@payment-client/helpers/Misc";

// styles
import styles from "./SecureCard.scss";
import { TransactionFailedAlert } from "../transaction-failed-alert";

//Analytics
import { onCvvScreenViewEvent, onPayPennyDropClickEvent } from "@payment-client/analytics/events/Card";

export interface SecureCardDataProps {
    newCardDetails?: ProceedNewCardDetails;
    savedCardDetails?: {
        nickName: string;
    };
    paymentMethod?: typeof VALID_PAYMENT_METHODS.NEW_CARD | typeof VALID_PAYMENT_METHODS.CARD;
    isNonSecureCard?: boolean;
}

type SecureCardPaymentProps = RouteProps<SecureCardDataProps>;

interface SecureCardContentProps {
    renderingDetails: Pick<CardDetails, "cardBrand" | "iconUrl" | "cardNumber">;
    cardDetails: CardDetails | ProceedNewCardDetails;
    data?: SecureCardDataProps;
    defaultCvv?: string;
}

const SecureCardContent = memo(function SecureCardContent(props: SecureCardContentProps): ReactElement {
    const { createTransactionInProgress, processingTransaction } = useContext(TransactionContext);
    const { checkoutState } = useContext(CheckoutContext);
    const { screenViewEvent, clickEvent } = useContext(AnalyticsContext);
    const onProcess = useOnProcess();

    const { renderingDetails, cardDetails, defaultCvv, data = {} } = props;
    const { isNonSecureCard = false, paymentMethod, savedCardDetails } = data;

    const onPay = useCallback(
        (details: CardDetails | ProceedNewCardDetails, extraParams: { cvv: string; nickname?: string }): void => {
            onPayPennyDropClickEvent(clickEvent);

            const nickName = savedCardDetails?.nickName;
            const { cardDetails, additionalParams } = getOnProcessParams(details, extraParams, nickName);
            onProcess({
                cardDetails,
                paymentMethod: paymentMethod || VALID_PAYMENT_METHODS.CARD,
                extraParams: additionalParams,
                isNonSecureCard,
            });
        },
        [clickEvent, isNonSecureCard, onProcess, paymentMethod, savedCardDetails],
    );

    const directProcessTransaction = useCallback(
        (cardDetails: CardDetails, paymentMethod: string, additionalParams: { [key: string]: string }): void => {
            onProcess({
                cardDetails,
                paymentMethod,
                extraParams: additionalParams,
                isNonSecureCard,
            });
        },
        [isNonSecureCard, onProcess],
    );

    //Analytics event
    useEffectOnce(() => {
        onCvvScreenViewEvent(renderingDetails.cardBrand, screenViewEvent);
        recordNREvent({
            eventType: NEWRELIC_TABLES.PAYMENT_MODES_SECURE_CARD,
        });
    });

    return (
        <PaymentModesWrapper title={""} headerClassName={styles.header} containerClassName={styles.container}>
            <VerifyCvv
                renderingDetails={renderingDetails}
                cardDetails={cardDetails}
                onPay={onPay}
                defaultCvv={defaultCvv}
                isNonSecureCard={isNonSecureCard}
            />
            {(createTransactionInProgress || processingTransaction) && <TransactionProgressLoader />}
            {checkoutState.type === CheckoutActionTypes.TRANSACTION_FAILED && (
                <TransactionFailedAlert
                    payAction={directProcessTransaction}
                    amountToPay={String(2)}
                    payload={checkoutState.payload}
                />
            )}
        </PaymentModesWrapper>
    );
});

export const SecureCard = memo(function SecureCard(props: SecureCardPaymentProps): ReactElement | null {
    const { data } = props;
    const { getAllCardsResponse, loadingPaymentModes } = useContext(PaymentModesContext);

    const getCardDetails = useCallback((): {
        cardDetails: CardDetails | ProceedNewCardDetails | null;
        renderingDetails: Pick<CardDetails, "cardNumber" | "iconUrl" | "cardBrand"> | null;
    } => {
        if (loadingPaymentModes) {
            return {
                cardDetails: null,
                renderingDetails: null,
            };
        }
        const paymentMethod = props.data?.paymentMethod || VALID_PAYMENT_METHODS.CARD;

        if (paymentMethod === VALID_PAYMENT_METHODS.NEW_CARD) {
            if (!data || !data.newCardDetails) {
                return {
                    cardDetails: null,
                    renderingDetails: null,
                };
            }

            return {
                cardDetails: data.newCardDetails,
                renderingDetails: {
                    cardBrand: data.newCardDetails.brand,
                    cardNumber: data?.newCardDetails?.cardNumber,
                    iconUrl: data?.newCardDetails.cardIcon?.id || "",
                },
            };
        } else {
            const cardFingerprint = GlobalHeaderState.getHeader(SwgyHeaderKeysList.cardFingerprint);
            const cardsList = getAllCardsResponse?.cards || [];
            const cardDetails = getSelectedCardDetails(cardFingerprint, cardsList);

            if (cardDetails == null) {
                return {
                    cardDetails: null,
                    renderingDetails: null,
                };
            }
            return {
                cardDetails,
                renderingDetails: {
                    cardBrand: cardDetails.cardBrand,
                    cardNumber: cardDetails.cardNumber,
                    iconUrl: cardDetails.iconUrl,
                },
            };
        }
    }, [getAllCardsResponse, loadingPaymentModes, props.data, data]);

    if (loadingPaymentModes) {
        return (
            <PaymentModesWrapper title={""}>
                <LoadingScreen />
            </PaymentModesWrapper>
        );
    }

    const { cardDetails, renderingDetails } = getCardDetails();

    if (!getAllCardsResponse || !cardDetails || !renderingDetails) {
        const action = getPaymentErrorScreenAction(PAYMENT_INITIAL_STATE.SECURE_CARD);

        return (
            <PaymentModesWrapper title={""}>
                <GenericError action={action} />
            </PaymentModesWrapper>
        );
    }

    return (
        <SecureCardContent
            cardDetails={cardDetails}
            data={data}
            renderingDetails={renderingDetails}
            defaultCvv={props.data?.newCardDetails?.cvv}
        />
    );
});
